import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { desenharPlacar } from '../components/Placar';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { avisoFinalDoScanPixel, desfechoDoEnvio, montarPlacar, ProgressoScanPixel, saiDoResultadoScanPixel } from '../services/scanPixel';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

const PAINEL = { x: 853, y: 238 };

/**
 * Scan Pixel — pontuação (Figma, quadros 35 e 36). Envia o resultado ao
 * servidor ao abrir; a colocação (e o bônus) é a que o servidor devolver.
 */
export class ScanPixelResultadoScene extends Scene {
    constructor () {
        super(SCENES.SCAN_RESULTADO);
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        const fonte = this.registry.get('fonte');
        this.progresso = new ProgressoScanPixel(Sessao.atual.participanteId);
        this.objetosPlacar = [];

        desenharPaginaLima(this, {
            titulo: t('scan.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0
        });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.AGUARDE, {}));

        this.add.text(64, 184, t('scan.resultado_manchete'), ARCADE_TEXT.manchete);
        this.add.rectangle(64, 273, 631, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);
        const { altura } = criarTextoRico(this, 64, 298,
            633, t('scan.resultado_texto_1'),
            ARCADE_TEXT.introducao, { entrelinha: 29 });
        criarTextoRico(this, 64, 298 + altura + 27, 633,
            t('scan.resultado_texto_2'), ARCADE_TEXT.introducao, { entrelinha: 29 });

        new BotaoArcade(this, 640, 711, { rotulo: t('geral.continuar'), aoClicar: () => this.abrirFinalizacao() });

        this.totalEnigmas = Math.max(5, this.progresso.estado.concluidos.length);
        this.regras = null;
        this.desenharPlacar();
        // Quanto vale cada enigma e cada bônus vem do servidor; sem resposta, valem os padrões.
        Promise.resolve(fonte.obterRegrasDePontuacao?.('scanpixel'))
            .then((regras) => {
                this.regras = regras ?? null;
                if (this.scene.isActive()) this.desenharPlacar();
            })
            .catch((falha) => console.warn('[ScanPixel] regras de pontuação indisponíveis.', falha?.message));

        // RN-SP-10..14: a ordem de chegada (e o bônus) é decidida pelo servidor.
        const cancelar = fonte.aoResultadoScanPixel(({ grupoId, colocacao }) => {
            if (grupoId && grupoId !== Sessao.atual.grupoId) return;
            this.progresso.marcarEnviado(colocacao);
            if (this.scene.isActive()) this.desenharPlacar();
        });
        this.events.once('shutdown', cancelar);
        // AP11: o aviso final não tem botão; o facilitador iniciar outra estação do grupo leva ao
        // Aguarde, que entra nela (a fase nova fica pendente até ele escutar).
        const cancelarEstacao = fonte.aoEstacao?.((aviso) => {
            if (saiDoResultadoScanPixel(aviso) && this.scene.isActive()) {
                this.scene.start(SCENES.AGUARDE, { concluida: 'scanpixel' });
            }
        });
        if (cancelarEstacao) this.events.once('shutdown', cancelarEstacao);

        const concluidos = this.progresso.estado.concluidos.length;
        if (this.progresso.estado.enviado) {
            // Resultado já aceito antes: só confirma o progresso, para o grupo poder finalizar.
            fonte.registrarProgresso?.('scanpixel', concluidos);
        } else {
            try {
                const { colocacao } = await fonte.enviarResultadoScanPixel({ ...this.progresso.envio(), concluidos });
                this.progresso.marcarEnviado(colocacao);
            } catch (falha) {
                const desfecho = desfechoDoEnvio(falha);
                if (desfecho === 'ja_sincronizado') {
                    // O grupo já tem resultado (por exemplo, depois de um reinício): busca a colocação dele.
                    const salvo = await Promise.resolve(fonte.lerResultadoScanPixel?.()).catch(() => null);
                    this.progresso.marcarEnviado(salvo?.colocacao);
                } else if (desfecho === 'aguardar_lider') {
                    // AP10: quem fecha o grupo é o líder; o bônus fica em "..." até a colocação dele chegar.
                    console.info('[ScanPixel] aguardando o resultado do líder do grupo.');
                } else {
                    // Fica pendente: a mesma chave de idempotência é reusada na próxima abertura.
                    console.error('[ScanPixel] resultado não enviado; fica pendente.', falha);
                }
            }
            if (this.scene.isActive()) this.desenharPlacar();
        }
    }

    desenharPlacar () {
        this.objetosPlacar.forEach((objeto) => objeto.destroy());
        const { estado } = this.progresso;
        const placar = montarPlacar({
            totalEnigmas: this.totalEnigmas, concluidos: estado.concluidos, colocacao: estado.colocacao,
            regras: this.regras
        });

        this.objetosPlacar = desenharPlacar(this, PAINEL.x, PAINEL.y, placar);
    }

    /** RI-SP-11 / RI-SP-12. */
    abrirFinalizacao () {
        if (this.modal) return;
        // AP11 (RN-AP-044): sem botão de saída — o tablet sai pelo facilitador (ver o `aoEstacao` do create). O que
        // existe é só o voltar à pontuação, que fecha o aviso e devolve o placar.
        this.modal = new ModalArcade(this, {
            ...avisoFinalDoScanPixel(),
            voltar: { rotulo: t('geral.voltar_pontuacao') },
            aoFechar: () => { this.modal = null; }
        });
    }
}
